import { useMutation } from '@apollo/client';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { TranslatableTextField } from '@ovComponents/2-component/translatableTextField/translatableTextField';
import { CreateNewModal } from '@ovComponents/3-pattern/createNewModal/createNewModal';
import { OrganizationSelect } from '@ovComponents/3-pattern/organizationSelect/organizationSelect';
import { UserContext } from '@providers/userContextProvider';
import { Box } from 'hephaestus/web';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CREATE_ORGANIZATION } from './newOrganizationModal.query';

export type NewOrganizationModalProp = {
  afterCreate: (newOrgId: string) => void;
};

export const NewOrganizationModal = ({
  afterCreate,
}: NewOrganizationModalProp) => {
  const { t } = useTranslation(['orgSettings']);
  const { activeOrganization } = useContext(UserContext);
  const [organization, setOrganization] = useState({
    translatedName: getLanguageDefaults(),
    subdomain: '',
    parentId: '',
    useParentRoleProfiles: true,
    useParentStaticPageWidgetConfigurations: true,
    useParentCustomFields: true,
    useParentIntegrations: true,
  });

  const [createOrganization, { loading }] = useMutation(CREATE_ORGANIZATION);

  useEffect(() => {
    setOrganization({ ...organization, parentId: activeOrganization.id ?? '' });
  }, [activeOrganization]);

  const createOrg = async () => {
    const response = await createOrganization({
      variables: { input: organization },
    });
    const newOrgId = response.data?.createOrganization.organization?.id;
    if (newOrgId) afterCreate(newOrgId);
  };

  return (
    <CreateNewModal
      loading={loading}
      title={t('newOrganizationModal.title')}
      onSubmit={createOrg}
      buttonVariant='tonal'
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <TranslatableTextField
          label={t('organizationDetails.name')}
          fullWidth
          value={organization.translatedName}
          onChange={(value) =>
            setOrganization({
              ...organization,
              translatedName: value,
            })
          }
        />
        <TextField
          label={t('organizationDetails.subdomain')}
          fullWidth
          value={organization.subdomain}
          onChange={(event: any) =>
            setOrganization({ ...organization, subdomain: event.target.value })
          }
        />
        <OrganizationSelect
          value={organization.parentId}
          label={t('organizationDetails.parent')}
          onChange={(event: any) =>
            setOrganization({ ...organization, parentId: event as string })
          }
        />
      </Box>
    </CreateNewModal>
  );
};
